/**
 * Inline SVG icon helpers — replaces all emoji usage in the benchmark UI.
 * Each function returns an SVG string (16×16, uses currentColor for theming).
 * The `cls` parameter adds extra CSS classes for sizing/alignment overrides.
 */

const svg = (inner: string, cls = "") =>
  `<svg class="icon ${cls}" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">${inner}</svg>`;

// ── Buttons ──────────────────────────────────────────────────

export const iconPlay = (cls = "") =>
  svg(`<path d="M4 2.5v11l9-5.5L4 2.5z" fill="currentColor"/>`, cls);

export const iconStop = (cls = "") =>
  svg(`<rect x="3" y="3" width="10" height="10" rx="1.5" fill="currentColor"/>`, cls);

export const iconPause = (cls = "") =>
  svg(
    `<rect x="4" y="2.5" width="3" height="11" rx="0.8" fill="currentColor"/>` +
    `<rect x="9" y="2.5" width="3" height="11" rx="0.8" fill="currentColor"/>`,
    cls,
  );

export const iconReplay = (cls = "") =>
  svg(
    `<path d="M2.5 8a5.5 5.5 0 1 1 1.1 3.3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>` +
    `<path d="M2 4.5V8h3.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>`,
    cls,
  );

// ── Lifecycle ────────────────────────────────────────────────

export const iconStart = (cls = "") =>
  svg(`<circle cx="8" cy="8" r="5" fill="currentColor" opacity="0.15"/><circle cx="8" cy="8" r="3" fill="currentColor"/>`, cls);

export const iconFinish = (cls = "") =>
  svg(
    `<path d="M3 2v12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>` +
    `<path d="M3 3h7.5c1.5 0 2.5 1 2.5 2.25S12 7.5 10.5 7.5H5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`,
    cls,
  );

export const iconError = (cls = "") =>
  svg(
    `<circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.2"/>` +
    `<path d="M8 4.5v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>` +
    `<circle cx="8" cy="11" r="0.75" fill="currentColor"/>`,
    cls,
  );

// ── Messages ─────────────────────────────────────────────────

export const iconChat = (cls = "") =>
  svg(
    `<path d="M2 3.5A1.5 1.5 0 0 1 3.5 2h9A1.5 1.5 0 0 1 14 3.5v6A1.5 1.5 0 0 1 12.5 11H6l-3 3v-3H3.5A1.5 1.5 0 0 1 2 9.5v-6z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/>` +
    `<path d="M5 5.5h6M5 8h4" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/>`,
    cls,
  );

export const iconPrompt = (cls = "") =>
  svg(
    `<rect x="2.5" y="1.5" width="11" height="13" rx="1.5" stroke="currentColor" stroke-width="1.2"/>` +
    `<path d="M5 5h6M5 8h6M5 11h3" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/>`,
    cls,
  );

// ── Subagent ─────────────────────────────────────────────────

export const iconFork = (cls = "") =>
  svg(
    `<circle cx="4" cy="4" r="1.8" stroke="currentColor" stroke-width="1.1"/>` +
    `<circle cx="12" cy="4" r="1.8" stroke="currentColor" stroke-width="1.1"/>` +
    `<circle cx="8" cy="12" r="1.8" stroke="currentColor" stroke-width="1.1"/>` +
    `<path d="M8 10.2V8c0-1.1.9-2 2-2h.2M5.8 5.8c.1.1.2.2.2.2 0 1.1.9 2 2 2v2" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/>`,
    cls,
  );

// ── Validation ───────────────────────────────────────────────

export const iconCheck = (cls = "") =>
  svg(
    `<circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.2" opacity="0.3"/>` +
    `<path d="M5 8.2l2 2 4-4.4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`,
    cls,
  );

export const iconCross = (cls = "") =>
  svg(
    `<circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.2" opacity="0.3"/>` +
    `<path d="M5.5 5.5l5 5M10.5 5.5l-5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>`,
    cls,
  );

// ── Status ───────────────────────────────────────────────────

export const iconInfo = (cls = "") =>
  svg(
    `<circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.2"/>` +
    `<path d="M8 7v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>` +
    `<circle cx="8" cy="5" r="0.75" fill="currentColor"/>`,
    cls,
  );

export const iconWarning = (cls = "") =>
  svg(
    `<path d="M8 1.5L1 14h14L8 1.5z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/>` +
    `<path d="M8 6v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>` +
    `<circle cx="8" cy="12" r="0.75" fill="currentColor"/>`,
    cls,
  );

// ── Tool icons ───────────────────────────────────────────────

export const iconRead = (cls = "") =>
  svg(
    `<path d="M2 2.5c2-.8 4-.5 6 1 2-1.5 4-1.8 6-1v10c-2-.5-4-.2-6 1-2-1.2-4-1.5-6-1V2.5z" stroke="currentColor" stroke-width="1.1" stroke-linejoin="round"/>` +
    `<path d="M8 3.5v10" stroke="currentColor" stroke-width="1" stroke-linecap="round"/>`,
    cls,
  );

export const iconWrite = (cls = "") =>
  svg(
    `<path d="M11.5 1.5l3 3-9 9H2.5v-3l9-9z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/>` +
    `<path d="M9.5 3.5l3 3" stroke="currentColor" stroke-width="1.1"/>`,
    cls,
  );

export const iconExec = (cls = "") =>
  svg(
    `<path d="M8.5 1L3 9h4.5L7 15l6-8H8.5L8.5 1z" stroke="currentColor" stroke-width="1.1" stroke-linejoin="round" fill="currentColor" fill-opacity="0.1"/>`,
    cls,
  );

export const iconSearch = (cls = "") =>
  svg(
    `<circle cx="7" cy="7" r="4.5" stroke="currentColor" stroke-width="1.3"/>` +
    `<path d="M10.5 10.5L14 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>`,
    cls,
  );

export const iconGlobe = (cls = "") =>
  svg(
    `<circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.1"/>` +
    `<ellipse cx="8" cy="8" rx="3" ry="6" stroke="currentColor" stroke-width="1"/>` +
    `<path d="M2 8h12M3 4.5h10M3 11.5h10" stroke="currentColor" stroke-width="0.8"/>`,
    cls,
  );

export const iconClock = (cls = "") =>
  svg(
    `<circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.2"/>` +
    `<path d="M8 4v4.5l3 1.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>`,
    cls,
  );

export const iconAttach = (cls = "") =>
  svg(
    `<path d="M7.5 4v7a2.5 2.5 0 0 0 5 0V4a4 4 0 0 0-8 0v8a5.5 5.5 0 0 0 11 0V4" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>`,
    cls,
  );

export const iconWrench = (cls = "") =>
  svg(
    `<path d="M10.5 2a4.5 4.5 0 0 0-4.2 6.1L2 12.5 3.5 14l4.4-4.3A4.5 4.5 0 1 0 10.5 2z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round" fill="currentColor" fill-opacity="0.07"/>`,
    cls,
  );

// ── Panel accent icons ───────────────────────────────────────

export const iconTreatment = (cls = "") =>
  svg(
    `<circle cx="8" cy="8" r="5" fill="var(--treatment-accent)" opacity="0.2"/>` +
    `<circle cx="8" cy="8" r="3" fill="var(--treatment-accent)"/>`,
    cls,
  );

export const iconBaseline = (cls = "") =>
  svg(
    `<circle cx="8" cy="8" r="5" fill="var(--baseline-accent)" opacity="0.2"/>` +
    `<circle cx="8" cy="8" r="3" fill="var(--baseline-accent)"/>`,
    cls,
  );

// ── Large idle icons (48px) ──────────────────────────────────

const svgLarge = (inner: string, cls = "") =>
  `<svg class="icon icon--lg ${cls}" width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">${inner}</svg>`;

export const iconTreatmentLg = (cls = "") =>
  svgLarge(
    `<circle cx="24" cy="24" r="16" fill="var(--treatment-accent)" opacity="0.12"/>` +
    `<circle cx="24" cy="24" r="9" fill="var(--treatment-accent)" opacity="0.25"/>` +
    `<circle cx="24" cy="24" r="5" fill="var(--treatment-accent)"/>`,
    cls,
  );

export const iconBaselineLg = (cls = "") =>
  svgLarge(
    `<circle cx="24" cy="24" r="16" fill="var(--baseline-accent)" opacity="0.12"/>` +
    `<circle cx="24" cy="24" r="9" fill="var(--baseline-accent)" opacity="0.25"/>` +
    `<circle cx="24" cy="24" r="5" fill="var(--baseline-accent)"/>`,
    cls,
  );
